import { useState, useEffect } from "react";
import "../styles/Body.css";
import { CardA, ScoreCard } from "./ui/Cards";
import TrendingMatrices from "./TrendingMatrices";
// import NumberVsCard from "./stats/NumberVsCard";
import ContaminationPlot from "./ContaminationPlot";
import { useCommonMatrices, useScores, useContaminationPlotData } from "../api";
import { useUserSettings, useUserLocations } from "../api/user";
import "../styles/styles.css"; // Import the CSS file

const Body = () => {
  // Initialize state variables
  const [now] = useState(new Date().setHours(23, 59, 59, 999));
  const [dateRange, setDateRange] = useState(7);
  const [pastStart, setPastStart] = useState(
    new Date(now - dateRange * 24 * 60 * 60 * 1000)
  );
  const [location, setLocation] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All");

  // Get user settings and locations
  const { data: settingsData, isFetching: isFetchingUserSettings } =
    useUserSettings();
  const { data: locationsData, isFetching: isFetchingUserLocations } =
    useUserLocations();

  // Set location name based on current location ID
  useEffect(() => {
    // if (locationsData && settingsData) {
    //   setLocation(
    //     locationsData.filter(
    //       (loc) => loc.locationId === settingsData.currentLoc
    //     )[0].name
    //   );
    // }

    if (locationsData && locationsData.length > 1) {
      setSelectedLocation("All");
    } else if (locationsData && locationsData.length === 1) {
      setSelectedLocation(locationsData[0].locationId);
    }
  }, [locationsData]);

  // Get common drugs and adverse reactions
  const {
    data: commonMatrices,
    isFetching: isFetchingCommonMatrices,
    isError: isErrorCommonMatrices,
  } = useCommonMatrices(pastStart, selectedLocation);

  const { data: scoresData, isFetching: isFetchingScores } = useScores(
    pastStart,
    now,
    new Date(pastStart.getTime() - dateRange * 24 * 60 * 60 * 1000),
    pastStart,
    selectedLocation
  );

  const {
    data: contaminationPlotData,
    isFetching: isFetchingContaminationPlotData,
  } = useContaminationPlotData(pastStart, selectedLocation);

  const handleDateRangeChange = (event) => {
    const selectedDateRange = parseInt(event.target.value, 10);
    setDateRange(selectedDateRange);

    const newPastStart = new Date(
      now - selectedDateRange * 24 * 60 * 60 * 1000
    );
    setPastStart(newPastStart);
  };

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };

  if (isFetchingUserSettings || isFetchingUserLocations) {
    return (
      <div className='d-flex justify-content-center align-items-center h-100'>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className='px-sm-3 px-lg-5 py-3 full-height'
      style={{ marginTop: "79px" }}
    >
      <div className='shadow card full-height'>
        <div className='card-body full-height d-flex flex-column justify-content-around'>
          <div className='d-flex justify-content-between flex-wrap'>
            <div>
              <h5 className='card-title'> Testing Insights</h5>
              <h6 className='card-subtitle mb-2 text-muted'>
                *data is for Portable Raman scans only
              </h6>
            </div>
            <div className='d-flex flex-wrap' style={{ marginBottom: "1rem" }}>
              <select
                className='form-select w-auto'
                id='dateRangeSelect'
                value={dateRange}
                onChange={handleDateRangeChange}
              >
                <option value='7'>Past 7 days</option>
                <option value='14'>Past 14 days</option>
                <option value='30'>Past 30 days</option>
                <option value='60'>Past 60 days</option>
                <option value='90'>Past 90 days</option>
              </select>

              {!isFetchingUserLocations && locationsData.length > 1 && (
                <select
                  className='form-select w-auto ms-2'
                  id='locationSelect'
                  value={selectedLocation}
                  onChange={handleLocationChange}
                >
                  <option key={"All"} value={"All"}>
                    All Locations
                  </option>
                  {locationsData.map((location) => (
                    <option
                      key={location.locationId}
                      value={location.locationId}
                    >
                      {location.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className='d-flex flex-column justify-content-evenly full-height '>
            <div className='row justify-content-evenly d-flex '>
              <div className='col-lg-6 col-sm-12 pe-lg-2 py-1 py-lg-0 scorecard-container'>
                <ScoreCard
                  title='Samples Tested'
                  help={`Total number of samples in the past ${dateRange} days`}
                  isFetchingScores={isFetchingScores}
                  value={scoresData?.results.currentSamples}
                  vsValue={scoresData?.results.prevSamples}
                />
              </div>
              <div className='col-lg-6 col-sm-12 px-lg-2 py-1 py-lg-0 scorecard-container'>
                <ScoreCard
                  title='Contamination Score'
                  help={`Percentage of tested samples containing the contaminant of interest in the past ${dateRange} days`}
                  isFetchingScores={isFetchingScores}
                  unit='%'
                  value={scoresData?.results.currentContaminantScore.toFixed(2)}
                  vsValue={scoresData?.results.prevContaminantScore.toFixed(2)}
                />
              </div>
            </div>
            <div
              className='row justify-content-evenly d-flex align-items-stretch flex-wrap'
              style={{ flexGrow: "2" }}
            >
              {/* Common Food Matrices */}
              <div className='col-12 col-xl-6 mt-2 plot-card-container full-height'>
                <CardA
                  icon='fas fa-chart-pie'
                  title='Common Food Matrices'
                  className='flex-fill'
                >
                  {isFetchingCommonMatrices ||
                  isFetchingScores ||
                  isFetchingContaminationPlotData ? (
                    <div className='spinner-border' role='status'>
                      <span className='sr-only'>Loading...</span>
                    </div>
                  ) : isErrorCommonMatrices ? (
                    <span>Error. Please try reloading the page.</span>
                  ) : (
                    <TrendingMatrices
                      commonMatrices={commonMatrices.slice(0, 10)}
                    />
                  )}
                </CardA>
              </div>

              <div className='col-12 col-xl-6 mt-2 plot-card-container full-height '>
                <CardA
                  icon='fas fa-chart-line'
                  title='Contaminant of Interest'
                  className='flex-fill'
                >
                  {isFetchingContaminationPlotData ||
                  isFetchingScores ||
                  isFetchingCommonMatrices ? (
                    <div className='spinner-border' role='status'>
                      <span className='sr-only'>Loading...</span>
                    </div>
                  ) : contaminationPlotData.length === 0 ? (
                    <div
                      className='d-flex justify-content-center align-items-center h-100 bg-light'
                      style={{ minWidth: "90%" }}
                    >
                      No data
                    </div>
                  ) : (
                    <ContaminationPlot
                      period={Number(dateRange)}
                      data={contaminationPlotData}
                    />
                  )}
                </CardA>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Body;
